import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Input,
  List,
  ListItem,
  Text,
  useOutsideClick,
} from '@chakra-ui/react';

// AutocompleteInput
// -----------------
// A lightweight, fully client-side autocomplete input. Renders a normal
// Chakra <Input/> and shows a dropdown of filtered suggestions returned
// by the supplied `getSuggestions(query)` function. The dropdown only
// appears once the user has typed at least one character and there are
// matches to show; otherwise the input behaves exactly like a plain
// Chakra Input so callers can drop it into existing forms with no
// layout regressions.
//
// Props:
//   value, onChange       Standard controlled input pair.
//   getSuggestions(q)     Returns an array of strings for the current query.
//   inputProps            Forwarded to the inner Chakra <Input/>.
//   inputRef              Optional ref forwarded to the inner <Input/>.
//   onSelect(value)       Optional callback invoked when a suggestion is chosen.
//   maxSuggestions        Optional cap on the number of suggestions shown.
//   dropdownProps         Optional Chakra <Box/> props applied to the dropdown.
const AutocompleteInput = ({
  value,
  onChange,
  getSuggestions,
  inputProps = {},
  inputRef,
  onSelect,
  maxSuggestions = 8,
  dropdownProps = {},
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const wrapperRef = useRef(null);

  useOutsideClick({
    ref: wrapperRef,
    handler: () => setIsOpen(false),
  });

  useEffect(() => {
    if (!value) {
      setSuggestions([]);
      return;
    }
    const next = (getSuggestions(value) || []).slice(0, maxSuggestions);
    setSuggestions(next);
    setHighlight(-1);
  }, [value, getSuggestions, maxSuggestions]);

  const handleSelect = useCallback(
    (selected) => {
      onChange({ target: { value: selected } });
      if (onSelect) onSelect(selected);
      setIsOpen(false);
    },
    [onChange, onSelect]
  );

  const handleKeyDown = (e) => {
    if (!isOpen || suggestions.length === 0) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) {
        setIsOpen(true);
        setHighlight(0);
        e.preventDefault();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && highlight < suggestions.length) {
        e.preventDefault();
        handleSelect(suggestions[highlight]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && suggestions.length > 0;

  return (
    <Box position="relative" w="full" ref={wrapperRef}>
      <Input
        {...inputProps}
        ref={inputRef}
        value={value}
        onChange={(e) => {
          onChange(e);
          setIsOpen(true);
        }}
        onFocus={(e) => {
          if (value && (getSuggestions(value) || []).length > 0) {
            setIsOpen(true);
          }
          if (inputProps.onFocus) inputProps.onFocus(e);
        }}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />
      {showDropdown && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={2}
          bg="white"
          borderRadius="xl"
          boxShadow="0 12px 40px rgba(0, 0, 0, 0.18)"
          border="1px solid"
          borderColor="gray.100"
          zIndex={20}
          overflow="hidden"
          maxH="320px"
          overflowY="auto"
          {...dropdownProps}
        >
          <List spacing={0}>
            {suggestions.map((s, idx) => (
              <ListItem
                key={s}
                px={4}
                py={2.5}
                cursor="pointer"
                bg={idx === highlight ? 'gray.100' : 'white'}
                _hover={{ bg: 'gray.100' }}
                onMouseDown={(e) => {
                  // Use mouseDown so the click registers before the
                  // input's blur handler closes the dropdown.
                  e.preventDefault();
                  handleSelect(s);
                }}
                onMouseEnter={() => setHighlight(idx)}
              >
                <Text fontSize="sm" color="gray.800" noOfLines={1}>
                  {s}
                </Text>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default AutocompleteInput;
